import React, { useState, useRef, useEffect } from 'react';
import { csAdminApi } from '../../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  DescriptionOutlined as DocumentIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const DOCUMENT_TYPES = [
  { value: 'minutes', label: 'Meeting Minutes' },
  { value: 'agenda', label: 'Meeting Agenda' },
  { value: 'resolution', label: 'Resolution' },
  { value: 'report', label: 'Report' },
  { value: 'presentation', label: 'Presentation' },
  { value: 'policy', label: 'Policy Document' },
  { value: 'charter', label: 'Committee Charter' },
  { value: 'other', label: 'Other' },
];

const ALLOWED_FILE_TYPES = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const UploadDocumentModal = ({ 
  open, 
  onClose, 
  onUpload, 
  committeeId, 
  committeeName,
  existingDocuments = [] 
}) => {
  const [files, setFiles] = useState([]);
  const [documentType, setDocumentType] = useState('');
  const [customType, setCustomType] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFiles([]);
      setDocumentType('');
      setCustomType('');
      setDescription('');
      setError(null);
      setUploadProgress(0);
    }
  }, [open]);

  const getFileIcon = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'pdf') return <PdfIcon sx={{ color: '#E53935' }} />;
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <ImageIcon sx={{ color: '#4CAF50' }} />;
    return <FileIcon sx={{ color: '#1976D2' }} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = [];
    const errors = [];

    selectedFiles.forEach((file) => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!ALLOWED_FILE_TYPES.includes(ext)) {
        errors.push(`${file.name}: File type not allowed`);
        return;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File size exceeds 10MB limit`);
        return;
      }

      // Check for duplicate names
      if (files.some(f => f.name === file.name)) {
        errors.push(`${file.name}: File already selected`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setFiles([...files, ...validFiles]);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
    setError(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = [];
    const errors = [];

    droppedFiles.forEach((file) => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!ALLOWED_FILE_TYPES.includes(ext)) {
        errors.push(`${file.name}: File type not allowed`);
        return;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File size exceeds 10MB limit`);
        return;
      }

      if (files.some(f => f.name === file.name)) {
        errors.push(`${file.name}: File already selected`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setFiles([...files, ...validFiles]);
    }
  };

  const validateForm = () => {
    if (files.length === 0) {
      setError('Please select at least one file to upload');
      return false;
    }
    if (!documentType) {
      setError('Please select a document type');
      return false;
    }
    if (documentType === 'other' && !customType.trim()) {
      setError('Please specify the document type');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setUploading(true);
    setError(null);

    try {
      const typeLabel = documentType === 'other'
        ? customType
        : DOCUMENT_TYPES.find(t => t.value === documentType)?.label;

      // Persist each file to csadmin_documents (owner = committee)
      const total = files.length || 1;
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('owner_type', 'COMMITTEE');
        formData.append('owner_id', committeeId ?? '');
        formData.append('doc_category', typeLabel || '');
        formData.append('document_name', file.name);
        formData.append('notes', description.trim());
        // eslint-disable-next-line no-await-in-loop
        await csAdminApi.uploadDocument(formData);
        setUploadProgress(Math.round(((i + 1) / total) * 100));
      }

      const uploadedDocuments = files.map((file, index) => ({
        id: Date.now() + index,
        name: file.name,
        type: typeLabel,
        typeCode: documentType === 'other' ? 'other' : documentType,
        uploadDate: new Date().toISOString(),
        size: formatFileSize(file.size),
        sizeBytes: file.size,
        description: description.trim(),
        uploadedBy: 'Current User', // Replace with actual user
      }));

      if (onUpload) {
        onUpload(uploadedDocuments);
      }

      handleClose();
    } catch (err) {
      setError('Failed to upload documents. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFiles([]);
      setDocumentType('');
      setCustomType('');
      setDescription('');
      setError(null);
      setUploadProgress(0);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DocumentIcon color="primary" />
            <Typography variant="h6" component="div">
              Upload Document
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: 'grey.500' }}
            disabled={uploading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Committee Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Uploading documents for:
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {committeeName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {existingDocuments.length} existing documents
          </Typography>
        </Box>

        {/* Drop Zone */}
        <Box
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          sx={{
            border: 2,
            borderStyle: 'dashed',
            borderColor: 'grey.300',
            borderRadius: 2,
            p: 4,
            mb: 3,
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            bgcolor: 'grey.50',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: uploading ? 'grey.50' : 'grey.100',
              borderColor: uploading ? 'grey.300' : 'primary.main',
            },
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            {uploading ? 'Uploading...' : 'Drag & drop files here or click to browse'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Max file size: 10MB • Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, JPG, PNG
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_FILE_TYPES.join(',')}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            disabled={uploading}
          />
        </Box>

        {/* Selected Files */}
        {files.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Selected Files ({files.length})
            </Typography>
            <List
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              {files.map((file, index) => (
                <ListItem
                  key={index}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <ListItemIcon>
                    {getFileIcon(file)}
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={formatFileSize(file.size)}
                    primaryTypographyProps={{ noWrap: true }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      disabled={uploading}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 1 }} />
            <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
              Uploading... {uploadProgress}%
            </Typography>
          </Box>
        )}

        {/* Document Type */}
        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel>Document Type</InputLabel>
          <Select
            value={documentType}
            label="Document Type"
            onChange={(e) => {
              setDocumentType(e.target.value);
              setError(null);
            }}
            disabled={uploading}
          >
            {DOCUMENT_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {documentType === 'other' && (
          <TextField
            fullWidth
            required
            label="Specify Document Type"
            value={customType}
            onChange={(e) => setCustomType(e.target.value)}
            disabled={uploading}
            placeholder="Enter document type"
            sx={{ mb: 2 }}
          />
        )}

        {/* Description */}
        <TextField
          fullWidth
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={uploading}
          multiline
          rows={2}
          placeholder="Add a brief description..."
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={uploading || files.length === 0 || !documentType}
          startIcon={<CloudUploadIcon />}
        >
          {uploading ? 'Uploading...' : `Upload ${files.length > 0 ? `(${files.length})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDocumentModal;
